import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Package, Search, CheckCircle, Upload, MapPin, Send,
  ArrowLeft, ArrowRight, AlertCircle, Image, X, Download, Printer,
  CreditCard, Building2, Gift, Copy, Check, Shield
} from 'lucide-react'
import LanguageSwitcher from '../components/LanguageSwitcher'

// URL base de la API - vacío para usar rutas relativas con el proxy de Vite
const API_BASE = ''

interface OrderData {
  order_id: number
  order_reference: string
  order_date: string
  order_total: number
  customer_id: number
  customer_name: string
  customer_email: string
  customer_phone: string | null
  customer_type: string
  customer_nif: string | null
  payment_method: string | null
  is_prestashop_customer: boolean
  pickup_disabled?: boolean
  shipping_address: {
    street: string
    postal_code: string
    city: string
    country_code: string
    phone?: string
  }
  products: Array<{
    product_id: number
    product_name: string
    product_reference: string
    quantity: number
    unit_price: number
    total_price: number
    image_url: string | null
  }>
}

interface SelectedProduct {
  product_id: number
  product_name: string
  product_reference: string
  quantity: number
  unit_price: number
  total_price: number
  image_url: string | null
}

interface PhotoFile {
  file: File
  preview: string
  type: 'product' | 'packaging'
}

function validateIban(iban: string): boolean {
  const clean = iban.replace(/\s/g, '').toUpperCase()
  if (clean.length < 15 || clean.length > 34) return false
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(clean)) return false
  const rearranged = clean.slice(4) + clean.slice(0, 4)
  const numeric = rearranged.replace(/[A-Z]/g, (ch) => String(ch.charCodeAt(0) - 55))
  let remainder = numeric
  while (remainder.length > 2) {
    const block = remainder.slice(0, 9)
    remainder = (parseInt(block, 10) % 97).toString() + remainder.slice(block.length)
  }
  return parseInt(remainder, 10) % 97 === 1
}

function formatIbanInput(value: string): string {
  const clean = value.replace(/\s/g, '')
  return clean.replace(/(.{4})/g, '$1 ').trim()
}

const LOCALE_MAP: Record<string, string> = { es: 'es-ES', pt: 'pt-PT', en: 'en-GB' }

function getApiError(detail: any, t: any): string {
  if (typeof detail === 'object' && detail?.code) {
    const key = `errors.api.${detail.code}`
    // Los params pueden venir en detail.params o directamente en detail
    const { code, message, params, ...rest } = detail
    const interpolation = { defaultValue: '', ...params, ...rest }
    const translated = t(key, interpolation)
    return translated || message || t('errors.generic')
  }
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) return detail.map((e: any) => e.msg || JSON.stringify(e)).join('; ')
  return t('errors.generic')
}

export default function PublicReturnsPage() {
  const { t, i18n } = useTranslation()
  const locale = LOCALE_MAP[i18n.language] || 'en-GB'

  const RETURN_REASONS = [
    { value: 'not_needed', label: t('reasons.not_needed') },
    { value: 'wrong_product', label: t('reasons.wrong_product') },
    { value: 'defective', label: t('reasons.defective') },
    { value: 'damaged', label: t('reasons.damaged') },
    { value: 'not_as_described', label: t('reasons.not_as_described') },
    { value: 'better_price', label: t('reasons.better_price') },
    { value: 'other', label: t('reasons.other') }
  ]

  const [step, setStep] = useState(0)
  const [caseType, setCaseType] = useState<'return' | 'warranty'>('return')

  // Step 0: Seleccion de accion (solicitar o consultar)
  // Step 1: Validacion
  const [validationTab, setValidationTab] = useState<'online' | 'invoice'>('online')
  // Tab 1: Pedido online
  const [orderRef, setOrderRef] = useState('')
  const [email, setEmail] = useState('')
  // Tab 2: Factura
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [taxId, setTaxId] = useState('')
  // Shared
  const [validating, setValidating] = useState(false)
  const [validationError, setValidationError] = useState('')
  const [orderData, setOrderData] = useState<OrderData | null>(null)

  // Step 2: Seleccion productos
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([])
  const [warrantyValidating, setWarrantyValidating] = useState(false)
  const [warrantyError, setWarrantyError] = useState('')

  // Step 3: Fotos
  const [photos, setPhotos] = useState<PhotoFile[]>([])
  const [acceptCondition, setAcceptCondition] = useState(false)

  // Step 4: Direccion
  const [address, setAddress] = useState({
    contact_name: '',
    street: '',
    postal_code: '',
    city: '',
    country_code: 'ES',
    phone: ''
  })

  // Step 5: Motivo y confirmacion
  const [reason, setReason] = useState('')
  const [reasonDetails, setReasonDetails] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [returnCode, setReturnCode] = useState('')
  const [hasPickupLabel, setHasPickupLabel] = useState(false)
  const [pickupTracking, setPickupTracking] = useState('')

  // Opcion de transporte para PVP
  const [shippingOption, setShippingOption] = useState<'own' | 'pickup'>('pickup')

  // Recogida MRW
  const [pickupDate, setPickupDate] = useState('')
  const [pickupTimeSlot, setPickupTimeSlot] = useState<'1' | '2'>('1')

  // Observaciones para la recogida
  const [pickupNotes, setPickupNotes] = useState('')

  // Coste de envío calculado por backend (para PVD)
  const [backendShippingCost, setBackendShippingCost] = useState<number>(0)
  const [shippingCostReason, setShippingCostReason] = useState<string>('')

  // Copiar codigo
  const [copied, setCopied] = useState(false)

  // Preferencia de reembolso (para pagos no tarjeta)
  const [refundPreference, setRefundPreference] = useState<'transfer' | 'store_credit' | null>(null)
  const [refundIban, setRefundIban] = useState('')
  const [refundIbanHolder, setRefundIbanHolder] = useState('')

  // Calculos
  const productAmount = selectedProducts.reduce((sum, p) => sum + p.total_price, 0)
  const isPVP = orderData?.customer_type === 'PVP'
  const isPVD = orderData?.customer_type === 'PVD'
  const isVIP = orderData?.customer_type === 'VIP'
  const isIberianCountry = ['ES', 'PT'].includes(address.country_code?.toUpperCase() || '')
  // Si el coste de recogida >= importe productos, forzar envío propio
  const pickupExceedsAmount = backendShippingCost > 0 && backendShippingCost >= productAmount
  // Cliente excluido del servicio de recogida
  const pickupDisabled = orderData?.pickup_disabled === true
  // Fuera de ES/PT, coste > importe, cliente excluido: solo envio propio
  // Garantía: PVP siempre propio, PVD con coste > 0 propio, PVD coste 0 y VIP pickup
  const effectiveShippingOption = (
    (caseType === 'warranty' && (isPVP || backendShippingCost > 0)) ||
    !isIberianCountry ||
    pickupExceedsAmount ||
    pickupDisabled
  ) ? 'own' : shippingOption
  // El coste viene del backend (shipping_cost en validate-order response)
  const shippingCost = effectiveShippingOption === 'pickup' ? backendShippingCost : 0
  const refundAmount = productAmount - shippingCost

  // Determinar si el pago fue con tarjeta
  const isCardPayment = orderData?.payment_method && ['redsys', 'stripe'].includes(orderData.payment_method)
  // Solo ofrecer cupón si el cliente existe en PrestaShop
  const canUseCoupon = orderData?.is_prestashop_customer === true

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }

  // Step 1: Validar pedido
  const handleValidate = async () => {
    // Validar campos segun la pestaña activa
    if (validationTab === 'online') {
      if (!orderRef.trim() || !email.trim()) {
        setValidationError(t('step1.errorMissingOrder'))
        return
      }
    } else {
      if (!invoiceNumber.trim() || !taxId.trim()) {
        setValidationError(t('step1.errorMissingInvoice'))
        return
      }
    }

    setValidating(true)
    setValidationError('')

    try {
      const requestBody = validationTab === 'online'
        ? {
            validation_type: 'order',
            order_reference: orderRef.trim(),
            customer_email: email.trim(),
            case_type: caseType
          }
        : {
            validation_type: 'invoice',
            invoice_number: invoiceNumber.trim(),
            tax_id: taxId.trim().toUpperCase().replace(/[\s\-\.]/g, ''),
            case_type: caseType
          }

      const response = await fetch(`${API_BASE}/api/returns/validate-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(getApiError(data.detail, t))
      }

      setOrderData(data)
      setAddress({
        contact_name: data.customer_name || '',
        street: data.shipping_address.street || '',
        postal_code: data.shipping_address.postal_code || '',
        city: data.shipping_address.city || '',
        country_code: data.shipping_address.country_code || 'ES',
        phone: data.shipping_address.phone || data.customer_phone || ''
      })
      // Capturar coste de envío calculado por backend
      setBackendShippingCost(data.shipping_cost ?? 0)
      setShippingCostReason(data.shipping_cost_reason ?? '')
      setStep(2)
    } catch (error: any) {
      setValidationError(error.message || t('step1.errorValidation'))
    } finally {
      setValidating(false)
    }
  }

  // Step 2→3: Validar garantía por familia antes de avanzar
  const handleProductsContinue = async () => {
    if (caseType !== 'warranty' || !orderData) {
      setStep(3)
      return
    }

    setWarrantyValidating(true)
    setWarrantyError('')

    try {
      const response = await fetch(`${API_BASE}/api/returns/validate-warranty-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_date: orderData.order_date,
          products: selectedProducts.map(p => ({
            product_id: p.product_id,
            product_name: p.product_name,
            product_reference: p.product_reference,
            quantity: p.quantity,
            unit_price: p.unit_price,
            total_price: p.total_price,
            image_url: p.image_url
          }))
        })
      })

      const data = await response.json()

      if (!response.ok) {
        setWarrantyError(getApiError(data.detail, t))
        return
      }

      setStep(3)
    } catch (error: any) {
      setWarrantyError(error.message || t('errors.generic'))
    } finally {
      setWarrantyValidating(false)
    }
  }

  // Step 2: Toggle producto
  const toggleProduct = (product: OrderData['products'][0]) => {
    setWarrantyError('')
    const isSelected = selectedProducts.some(p => p.product_reference === product.product_reference)

    if (isSelected) {
      setSelectedProducts(prev => prev.filter(p => p.product_reference !== product.product_reference))
    } else {
      setSelectedProducts(prev => [...prev, {
        product_id: product.product_id,
        product_name: product.product_name,
        product_reference: product.product_reference,
        quantity: product.quantity > 1 ? 1 : product.quantity,
        unit_price: product.unit_price,
        total_price: product.quantity > 1 ? product.unit_price : product.total_price,
        image_url: product.image_url
      }])
    }
  }

  // Step 2: Cambiar cantidad a devolver
  const updateReturnQuantity = (productRef: string, newQty: number, maxQty: number) => {
    const qty = Math.max(1, Math.min(newQty, maxQty))
    setSelectedProducts(prev => prev.map(p =>
      p.product_reference === productRef
        ? { ...p, quantity: qty, total_price: +(p.unit_price * qty).toFixed(2) }
        : p
    ))
  }

  // Step 3: Manejar fotos
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>, type: 'product' | 'packaging') => {
    const files = e.target.files
    if (!files) return

    const newPhotos: PhotoFile[] = []
    Array.from(files).forEach(file => {
      if (file.type.startsWith('image/')) {
        newPhotos.push({
          file,
          preview: URL.createObjectURL(file),
          type
        })
      }
    })

    setPhotos(prev => [...prev, ...newPhotos])
    e.target.value = ''
  }

  const removePhoto = (index: number) => {
    setPhotos(prev => {
      const photo = prev[index]
      URL.revokeObjectURL(photo.preview)
      return prev.filter((_, i) => i !== index)
    })
  }

  // Step 5: Enviar solicitud
  const handleSubmit = async () => {
    if (caseType === 'warranty') {
      // Garantía: solo necesita descripción del defecto
      if (!reasonDetails.trim()) {
        setSubmitError(t('step5.errorMissingDefectDesc', 'Describe el defecto del producto'))
        return
      }
      // Si tiene recogida, necesita fecha
      if (effectiveShippingOption === 'pickup' && !pickupDate) {
        setSubmitError(t('step5.errorMissingPickupDate', 'Selecciona una fecha de recogida'))
        return
      }
    } else {
      // Devolución: necesita motivo + método de reembolso
      if (!reason) {
        setSubmitError(t('step5.errorMissingReason'))
        return
      }
    }

    // Validar preferencia de reembolso solo para devoluciones
    // Si no puede usar cupón, forzar transferencia
    const effectivePreference = caseType === 'return' && !isCardPayment ? (!canUseCoupon ? 'transfer' : refundPreference) : null
    if (caseType === 'return' && !isCardPayment && !effectivePreference) {
      setSubmitError(t('step5.errorMissingRefundMethod'))
      return
    }

    if (effectivePreference === 'transfer') {
      if (!refundIban) {
        setSubmitError(t('step5.errorMissingIban'))
        return
      }
      if (!validateIban(refundIban)) {
        setSubmitError(t('step5.errorInvalidIban'))
        return
      }
      if (!refundIbanHolder.trim()) {
        setSubmitError(t('step5.errorMissingIbanHolder'))
        return
      }
    }

    setSubmitting(true)
    setSubmitError('')

    try {
      // Crear la solicitud - endpoint diferente para garantía
      const endpoint = caseType === 'warranty'
        ? `${API_BASE}/api/returns/warranty-request`
        : `${API_BASE}/api/returns/request`

      const requestBody = caseType === 'warranty'
        ? {
            order_id: orderData!.order_id,
            order_reference: orderData!.order_reference,
            order_date: orderData!.order_date,
            customer_id: orderData!.customer_id,
            customer_email: orderData!.customer_email,
            customer_name: orderData!.customer_name,
            customer_phone: orderData!.customer_phone,
            customer_type: orderData!.customer_type,
            customer_nif: orderData!.customer_nif || undefined,
            products: selectedProducts,
            reason_details: reasonDetails,
            payment_method: orderData!.payment_method || undefined,
            language: i18n.language,
            pickup_address: effectiveShippingOption === 'pickup' ? address : undefined,
            shipping_option: effectiveShippingOption,
            pickup_date: effectiveShippingOption === 'pickup' ? pickupDate : undefined,
            pickup_time_slot: effectiveShippingOption === 'pickup' ? pickupTimeSlot : undefined,
            pickup_notes: effectiveShippingOption === 'pickup' ? pickupNotes : undefined,
          }
        : {
            order_id: orderData!.order_id,
            order_reference: orderData!.order_reference,
            order_date: orderData!.order_date,
            customer_id: orderData!.customer_id,
            customer_email: orderData!.customer_email,
            customer_name: orderData!.customer_name,
            customer_phone: orderData!.customer_phone,
            customer_type: orderData!.customer_type,
            customer_nif: orderData!.customer_nif || undefined,
            pickup_address: address,
            products: selectedProducts,
            reason: reason,
            reason_details: reasonDetails || undefined,
            shipping_option: effectiveShippingOption,
            pickup_notes: pickupNotes || undefined,
            pickup_date: effectiveShippingOption === 'pickup' ? pickupDate : undefined,
            pickup_time_slot: effectiveShippingOption === 'pickup' ? pickupTimeSlot : undefined,
            shipping_cost_override: (isPVD && shippingCost > 0) ? shippingCost : undefined,
            payment_method: orderData!.payment_method || undefined,
            refund_iban: refundIban || undefined,
            refund_iban_holder: refundIbanHolder.trim() || undefined,
            refund_preference: effectivePreference || undefined,
            language: i18n.language
          }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(getApiError(data.detail, t))
      }

      // Subir fotos si hay
      if (photos.length > 0) {
        const formData = new FormData()
        formData.append('photo_type', 'product')
        photos.forEach(photo => {
          formData.append('files', photo.file)
        })

        try {
          const photoResponse = await fetch(`${API_BASE}/api/returns/${data.return_code}/photos`, {
            method: 'POST',
            body: formData
          })

          if (!photoResponse.ok) {
            console.error('Error subiendo fotos:', await photoResponse.text())
          }
        } catch (photoError) {
          console.error('Error subiendo fotos:', photoError)
        }
      }

      setReturnCode(data.return_code)

      // Si hay recogida con etiqueta, guardar info y abrir etiqueta
      if (data.pickup?.success && data.pickup?.tracking_number) {
        setHasPickupLabel(true)
        setPickupTracking(data.pickup.tracking_number)

        // Abrir etiqueta en nueva ventana automáticamente
        window.open(`${API_BASE}/api/returns/${data.return_code}/label`, '_blank')
      }

      setStep(6)
    } catch (error: any) {
      setSubmitError(error.message || t('step5.errorSubmit'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-600/20">
                <span className="text-white font-bold text-lg">J</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">JVS Informatica</h1>
                <p className="text-sm text-slate-500">{t('common.portalTitle')}</p>
              </div>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Progress - solo mostrar en pasos 1-5 */}
      {step > 0 && step < 6 && (() => {
        const stepIcons = [Search, Package, Image, MapPin, Send]
        const stepLabels = [t('steps.verify'), t('steps.products'), t('steps.photos'), t('steps.address'), t('steps.confirm')]
        return (
          <div className="max-w-3xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-2">
              {[1, 2, 3, 4, 5].map(s => {
                const StepIcon = stepIcons[s - 1]
                const isCompleted = s < step
                const isCurrent = s === step
                return (
                  <div key={s} className={`flex items-center ${s < 5 ? 'flex-1' : ''}`}>
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                        isCompleted
                          ? 'bg-indigo-600 text-white'
                          : isCurrent
                            ? 'bg-indigo-600 text-white ring-4 ring-indigo-100 animate-pulse-soft'
                            : 'bg-slate-200 text-slate-400'
                      }`}
                    >
                      {isCompleted ? <CheckCircle className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                    </div>
                    {s < 5 && (
                      <div className={`flex-1 h-1 mx-2 rounded-full transition-colors ${
                        s < step ? 'bg-indigo-600' : 'bg-slate-200'
                      }`} />
                    )}
                  </div>
                )
              })}
            </div>
            <div className="flex justify-between text-xs">
              {stepLabels.map((label, i) => (
                <span key={i} className={`font-medium transition-colors ${
                  i + 1 <= step ? 'text-indigo-600' : 'text-slate-400'
                }`}>
                  {label}
                </span>
              ))}
            </div>
          </div>
        )
      })()}

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Step 0: Seleccion de accion */}
        {step === 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900">{t('step0.title')}</h2>
              <p className="text-slate-500 mt-2">
                {t('step0.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Opcion: Solicitar devolucion */}
              <button
                onClick={() => { setCaseType('return'); setStep(1) }}
                className="group flex flex-col items-center p-8 bg-gradient-to-br from-indigo-50 to-indigo-100 border-2 border-indigo-200 rounded-2xl hover:border-indigo-400 hover:shadow-lg transition-all"
              >
                <div className="w-20 h-20 bg-indigo-600 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Package className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{t('step0.requestReturn')}</h3>
                <p className="text-slate-500 text-center text-sm">
                  {t('step0.requestReturnDesc')}
                </p>
              </button>

              {/* Opcion: Solicitar garantia */}
              <button
                onClick={() => { setCaseType('warranty'); setStep(1) }}
                className="group flex flex-col items-center p-8 bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-200 rounded-2xl hover:border-amber-400 hover:shadow-lg transition-all"
              >
                <div className="w-20 h-20 bg-amber-600 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Shield className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{t('step0.requestWarranty', 'Solicitar garantia')}</h3>
                <p className="text-slate-500 text-center text-sm">
                  {t('step0.requestWarrantyDesc', 'Producto defectuoso? Gestiona la garantia de tu compra')}
                </p>
              </button>

              {/* Opcion: Consultar estado */}
              <a
                href="/returns/track"
                className="group flex flex-col items-center p-8 bg-gradient-to-br from-emerald-50 to-emerald-100 border-2 border-emerald-200 rounded-2xl hover:border-emerald-400 hover:shadow-lg transition-all"
              >
                <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Search className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{t('step0.checkReturn')}</h3>
                <p className="text-slate-500 text-center text-sm">
                  {t('step0.checkReturnDesc')}
                </p>
              </a>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-200 text-center">
              <p className="text-sm text-slate-500">
                {t('step0.helpText')}{' '}
                <a
                  href="https://wa.me/34644464960?text=Hola,%20tengo%20una%20consulta%20sobre%20devoluciones"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 hover:underline font-medium"
                >
                  WhatsApp
                </a>
                {' '}{t('step0.orPhone')}{' '}
                <a href="tel:+34942719735" className="text-indigo-600 hover:underline font-medium">
                  942 71 97 35
                </a>
              </p>
            </div>
          </div>
        )}

        {/* Step 1: Validacion */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up">
            <button
              onClick={() => setStep(0)}
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('common.backToStart')}
            </button>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900">{t('step1.title')}</h2>
              <p className="text-slate-500 mt-2">
                {t('step1.subtitle')}
              </p>
            </div>

            {/* Selector de tipo de compra */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {/* Opcion: Tienda online */}
              <button
                onClick={() => { setValidationTab('online'); setValidationError('') }}
                className={`p-5 rounded-xl border-2 text-left transition-all ${
                  validationTab === 'online'
                    ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${
                    validationTab === 'online' ? 'bg-indigo-100' : 'bg-slate-100'
                  }`}>
                    <svg className={`w-6 h-6 ${validationTab === 'online' ? 'text-indigo-600' : 'text-slate-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className={`font-semibold ${validationTab === 'online' ? 'text-indigo-900' : 'text-slate-900'}`}>
                      {t('step1.onlineTitle')}
                    </p>
                    <p className={`text-sm mt-1 ${validationTab === 'online' ? 'text-indigo-600' : 'text-slate-500'}`}>
                      {t('step1.onlineDesc')}
                    </p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    validationTab === 'online'
                      ? 'border-indigo-500 bg-indigo-500'
                      : 'border-slate-300'
                  }`}>
                    {validationTab === 'online' && (
                      <CheckCircle className="w-4 h-4 text-white" />
                    )}
                  </div>
                </div>
              </button>

              {/* Opcion: Factura */}
              <button
                onClick={() => { setValidationTab('invoice'); setValidationError('') }}
                className={`p-5 rounded-xl border-2 text-left transition-all ${
                  validationTab === 'invoice'
                    ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${
                    validationTab === 'invoice' ? 'bg-indigo-100' : 'bg-slate-100'
                  }`}>
                    <svg className={`w-6 h-6 ${validationTab === 'invoice' ? 'text-indigo-600' : 'text-slate-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className={`font-semibold ${validationTab === 'invoice' ? 'text-indigo-900' : 'text-slate-900'}`}>
                      {t('step1.invoiceTitle')}
                    </p>
                    <p className={`text-sm mt-1 ${validationTab === 'invoice' ? 'text-indigo-600' : 'text-slate-500'}`}>
                      {t('step1.invoiceDesc')}
                    </p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    validationTab === 'invoice'
                      ? 'border-indigo-500 bg-indigo-500'
                      : 'border-slate-300'
                  }`}>
                    {validationTab === 'invoice' && (
                      <CheckCircle className="w-4 h-4 text-white" />
                    )}
                  </div>
                </div>
              </button>
            </div>

            {/* Formulario segun opcion seleccionada */}
            <div className={`rounded-xl p-6 mt-2 ${
              validationTab === 'online'
                ? 'bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100'
                : 'bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100'
            }`}>
              {/* Cabecera del formulario */}
              <div className="flex items-center gap-3 mb-5">
                <div className={`p-2 rounded-lg ${
                  validationTab === 'online' ? 'bg-indigo-100' : 'bg-amber-100'
                }`}>
                  {validationTab === 'online' ? (
                    <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                </div>
                <div>
                  <h3 className={`font-semibold ${
                    validationTab === 'online' ? 'text-indigo-900' : 'text-amber-900'
                  }`}>
                    {validationTab === 'online' ? t('step1.onlineFormTitle') : t('step1.invoiceFormTitle')}
                  </h3>
                  <p className={`text-xs ${
                    validationTab === 'online' ? 'text-indigo-600' : 'text-amber-600'
                  }`}>
                    {validationTab === 'online'
                      ? t('step1.onlineFormDesc')
                      : t('step1.invoiceFormDesc')}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Formulario: Pedido online */}
                {validationTab === 'online' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-indigo-900 mb-2">
                        {t('step1.orderNumber')}
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={orderRef}
                        onChange={(e) => setOrderRef(e.target.value.replace(/\D/g, ''))}
                        placeholder="Ej: 123456"
                        className="w-full px-4 py-3 rounded-xl border border-indigo-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white text-slate-900 placeholder:text-slate-400"
                      />
                      <p className="text-xs text-indigo-500 mt-1">
                        {t('step1.orderNumberHelp')}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-indigo-900 mb-2">
                        {t('step1.purchaseEmail')}
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="tu@email.com"
                        className="w-full px-4 py-3 rounded-xl border border-indigo-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white text-slate-900 placeholder:text-slate-400"
                      />
                    </div>
                  </>
                )}

                {/* Formulario: Factura */}
                {validationTab === 'invoice' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-amber-900 mb-2">
                        {t('step1.invoiceNumber')}
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={invoiceNumber}
                        onChange={(e) => setInvoiceNumber(e.target.value.replace(/\D/g, ''))}
                        placeholder="Ej: 1234"
                        className="w-full px-4 py-3 rounded-xl border border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white text-slate-900 placeholder:text-slate-400"
                      />
                      <p className="text-xs text-amber-600 mt-1">
                        {t('step1.invoiceNumberHelp')}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-amber-900 mb-2">
                        {t('step1.taxId')}
                      </label>
                      <input
                        type="text"
                        value={taxId}
                        onChange={(e) => setTaxId(e.target.value)}
                        placeholder="Ej: 12345678A o B12345678"
                        className="w-full px-4 py-3 rounded-xl border border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white text-slate-900 placeholder:text-slate-400"
                      />
                      <p className="text-xs text-amber-600 mt-1">
                        {t('step1.taxIdHelp')}
                      </p>
                    </div>
                  </>
                )}

                {validationError && (
                  <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 border border-red-200">
                    <AlertCircle className="w-5 h-5 shrink-0" />
                    <span className="text-sm">{validationError}</span>
                  </div>
                )}

                <button
                  onClick={handleValidate}
                  disabled={validating}
                  className={`w-full py-3 text-white rounded-xl disabled:opacity-50 flex items-center justify-center gap-2 transition-colors ${
                    validationTab === 'online'
                      ? 'bg-indigo-600 hover:bg-indigo-700'
                      : 'bg-amber-600 hover:bg-amber-700'
                  }`}
                >
                  {validating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      {t('common.verifying')}
                    </>
                  ) : (
                    <>
                      {t('common.continue')}
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Seleccion productos */}
        {step === 2 && orderData && (
          <div className="bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up">
            <h2 className="text-xl font-bold text-slate-900 mb-2">{t('step2.title')}</h2>
            <p className="text-slate-500 mb-6">{t('step2.orderRef', { ref: orderData.order_reference })}</p>

            <div className="space-y-3 mb-6">
              {orderData.products.map(product => {
                const isSelected = selectedProducts.some(p => p.product_reference === product.product_reference)
                const selectedItem = selectedProducts.find(p => p.product_reference === product.product_reference)
                return (
                  <div
                    key={product.product_reference}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <button
                      onClick={() => toggleProduct(product)}
                      className="w-full text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          isSelected ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'
                        }`}>
                          {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                        </div>
                        {product.image_url && (
                          <img src={product.image_url} alt="" className="w-16 h-16 object-cover rounded" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{product.product_name}</p>
                          <p className="text-sm text-slate-500">{t('common.ref')} {product.product_reference} | {t('common.orderQuantity')} {product.quantity}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-slate-900">{formatMoney(isSelected ? selectedItem!.total_price : product.total_price)}</p>
                        </div>
                      </div>
                    </button>
                    {/* Selector de cantidad cuando hay mas de 1 unidad */}
                    {isSelected && product.quantity > 1 && (
                      <div className="flex items-center gap-3 mt-3 ml-10 pl-1">
                        <span className="text-sm text-slate-600">{t('step2.unitsToReturn')}</span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); updateReturnQuantity(product.product_reference, selectedItem!.quantity - 1, product.quantity) }}
                            className="w-8 h-8 rounded-lg bg-slate-200 hover:bg-slate-300 flex items-center justify-center text-slate-700 font-bold"
                          >
                            -
                          </button>
                          <span className="w-10 text-center font-semibold text-slate-900">{selectedItem!.quantity}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); updateReturnQuantity(product.product_reference, selectedItem!.quantity + 1, product.quantity) }}
                            className="w-8 h-8 rounded-lg bg-slate-200 hover:bg-slate-300 flex items-center justify-center text-slate-700 font-bold"
                          >
                            +
                          </button>
                        </div>
                        <span className="text-xs text-slate-400">{t('common.of')} {product.quantity}</span>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Info envío propio para garantías (solo cuando no hay recogida) */}
            {selectedProducts.length > 0 && caseType === 'warranty' && effectiveShippingOption === 'own' && (
              <div className="mb-6">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Package className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">{t('step2.warrantyOwnShippingTitle', 'Envio del producto')}</p>
                      <p className="text-sm text-amber-700 mt-1">{t('step2.warrantyOwnShippingDesc', 'Para evaluar la garantia, necesitamos que nos envies el producto. En el siguiente paso te indicaremos la direccion de envio.')}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Info recogida gratuita para garantías PVD/VIP */}
            {selectedProducts.length > 0 && caseType === 'warranty' && isIberianCountry && isVIP && !pickupDisabled && (
              <div className="mb-6">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <p className="text-sm text-green-800">{t('step2.warrantyVipFreePickup', 'Como cliente VIP, programaremos una recogida gratuita en tu domicilio para evaluar la garantia.')}</p>
                </div>
              </div>
            )}
            {selectedProducts.length > 0 && caseType === 'warranty' && isIberianCountry && isPVD && backendShippingCost === 0 && !pickupDisabled && (
              <div className="mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-blue-800">{t('step2.warrantyPvdFreePickup', 'Programaremos una recogida gratuita en tu domicilio para evaluar la garantia.')}</p>
                </div>
              </div>
            )}

            {/* Selector de opcion de transporte - solo para devoluciones */}
            {selectedProducts.length > 0 && caseType === 'return' && (
              <div className="mb-6">
                {/* Banner: cliente excluido del servicio de recogida */}
                {pickupDisabled && isIberianCountry && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">{t('step2.pickupDisabledTitle')}</p>
                        <p className="text-sm text-amber-700 mt-1">{t('step2.pickupDisabledDesc')}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Banner informativo para PVD/VIP (solo si recogida no deshabilitada) */}
                {isIberianCountry && isVIP && !pickupDisabled && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                    <p className="text-sm text-green-800">{t('step2.vipFreeInfo')}</p>
                  </div>
                )}
                {isIberianCountry && isPVD && shippingCost === 0 && !pickupDisabled && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                    <p className="text-sm text-blue-800">{t('step2.pvdFreeInfo')}</p>
                  </div>
                )}
                {isIberianCountry && isPVD && shippingCost > 0 && !pickupDisabled && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                    <p className="text-sm text-amber-800">{t('step2.pvdPaidInfo', { cost: shippingCost.toFixed(2).replace('.', ',') })}</p>
                  </div>
                )}

                {/* Solo PVP ibérico (sin exclusion) ve los botones de eleccion */}
                {isPVP && isIberianCountry && !pickupExceedsAmount && !pickupDisabled && (
                  <>
                    <label className="block text-sm font-medium text-slate-700 mb-3">{t('step2.shippingQuestion')}</label>
                    <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                      {/* Opcion: Transporte propio */}
                      <button
                        type="button"
                        onClick={() => setShippingOption('own')}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          shippingOption === 'own'
                            ? 'border-green-500 bg-green-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${shippingOption === 'own' ? 'bg-green-100' : 'bg-slate-100'}`}>
                            <Package className={`w-5 h-5 ${shippingOption === 'own' ? 'text-green-600' : 'text-slate-500'}`} />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900">{t('step2.ownShipping')}</p>
                            <p className="text-sm text-slate-500 mt-1">{t('step2.ownShippingDesc')}</p>
                            <p className="text-sm font-medium text-green-600 mt-2">{t('step2.fullRefund')}</p>
                          </div>
                          {shippingOption === 'own' && <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />}
                        </div>
                      </button>

                      {/* Opcion: Recogida MRW a domicilio */}
                      <button
                        type="button"
                        onClick={() => setShippingOption('pickup')}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          shippingOption === 'pickup'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${shippingOption === 'pickup' ? 'bg-blue-100' : 'bg-slate-100'}`}>
                            <Send className={`w-5 h-5 ${shippingOption === 'pickup' ? 'text-blue-600' : 'text-slate-500'}`} />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900">{t('step2.pickupMRW')}</p>
                            <p className="text-sm text-slate-500 mt-1">{t('step2.pickupMRWDesc')}</p>
                            <p className="text-sm mt-2">
                              {backendShippingCost > 0 ? (
                                <span className="font-medium text-red-600">-{backendShippingCost.toFixed(2).replace('.', ',')} EUR</span>
                              ) : (
                                <span className="font-medium text-green-600">{t('step2.free')}</span>
                              )}
                            </p>
                          </div>
                          {shippingOption === 'pickup' && <CheckCircle className="w-5 h-5 text-blue-500 shrink-0" />}
                        </div>
                      </button>
                    </div>

                    {isPVP && shippingCost > 0 && shippingOption === 'pickup' && (
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mt-3">
                        <p className="text-sm text-slate-600">{t('step2.pvpPickupCost', { cost: shippingCost.toFixed(2).replace('.', ',') })}</p>
                      </div>
                    )}
                  </>
                )}

                {/* Aviso: recogida no disponible porque el coste supera el importe */}
                {isPVP && isIberianCountry && pickupExceedsAmount && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800">{t('step2.pickupExceedsAmount', { cost: backendShippingCost.toFixed(2).replace('.', ',') })}</p>
                    </div>
                  </div>
                )}

                {!isIberianCountry && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                    <p className="text-sm text-blue-800">{t('step2.internationalNote')}</p>
                  </div>
                )}
              </div>
            )}

            {selectedProducts.length > 0 && caseType === 'return' && (
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-500">{t('step2.productAmount')}</span>
                  <span className="font-medium">{formatMoney(productAmount)}</span>
                </div>
                {shippingCost > 0 && (
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-500">{t('step2.shippingCost')}</span>
                    <span className="font-medium text-red-600">-{formatMoney(shippingCost)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold border-t border-slate-200 pt-2 mt-2">
                  <span>{t('step2.estimatedRefund')}</span>
                  <span className="text-green-600">{formatMoney(refundAmount)}</span>
                </div>
              </div>
            )}

            {warrantyError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                {warrantyError}
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-3 text-slate-600 hover:bg-slate-100 rounded-xl flex items-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                {t('common.back')}
              </button>
              <button
                onClick={handleProductsContinue}
                disabled={selectedProducts.length === 0 || warrantyValidating}
                className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {warrantyValidating ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('step2.validatingWarranty', 'Verificando garantía...')}
                  </>
                ) : (
                  <>
                    {t('common.continue')}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Fotos */}
        {step === 3 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up">
            <h2 className="text-xl font-bold text-slate-900 mb-2">{t('step3.title')}</h2>
            <p className="text-slate-500 mb-4" dangerouslySetInnerHTML={{ __html: t('step3.subtitle') }} />

            {/* Aviso de condiciones obligatorias */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800 mb-2">{caseType === 'warranty' ? t('step3.warrantyConditionsTitle') : t('step3.conditionsTitle')}</p>
                  <ul className="text-sm text-amber-700 space-y-1.5">
                    {caseType === 'warranty' ? (
                      <>
                        <li dangerouslySetInnerHTML={{ __html: '• ' + t('step3.warrantyCondition1') }} />
                        <li dangerouslySetInnerHTML={{ __html: '• ' + t('step3.warrantyCondition2') }} />
                        <li dangerouslySetInnerHTML={{ __html: '• ' + t('step3.warrantyCondition3') }} />
                      </>
                    ) : (
                      <>
                        <li dangerouslySetInnerHTML={{ __html: '• ' + t('step3.condition1') }} />
                        <li dangerouslySetInnerHTML={{ __html: '• ' + t('step3.condition2') }} />
                        <li dangerouslySetInnerHTML={{ __html: '• ' + t('step3.condition3') }} />
                        <li dangerouslySetInnerHTML={{ __html: '• ' + t('step3.condition4') }} />
                      </>
                    )}
                  </ul>
                  <p className="text-sm text-amber-800 font-medium mt-3">
                    {caseType === 'warranty' ? t('step3.warrantyConditionWarning') : t('step3.conditionWarning')}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handlePhotoUpload(e, 'product')}
                  className="hidden"
                  id="photo-upload"
                />
                <label
                  htmlFor="photo-upload"
                  className="cursor-pointer"
                >
                  <div className="inline-flex p-3 bg-slate-100 rounded-full mb-3">
                    <Upload className="w-6 h-6 text-slate-600" />
                  </div>
                  <p className="font-medium text-slate-900">{t('step3.uploadCta')}</p>
                  <p className="text-sm text-slate-500 mt-1">{t('step3.uploadDrag')}</p>
                  <p className="text-xs text-red-500 mt-2">{t('step3.uploadRequired')}</p>
                </label>
              </div>

              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {photos.map((photo, idx) => (
                    <div key={idx} className="relative">
                      <img
                        src={photo.preview}
                        alt=""
                        className="w-full aspect-square object-cover rounded-lg"
                      />
                      <button
                        onClick={() => removePhoto(idx)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Checkbox de aceptacion de condiciones */}
            <label className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl cursor-pointer mb-6 hover:bg-slate-100 transition-colors">
              <input
                type="checkbox"
                checked={acceptCondition}
                onChange={(e) => setAcceptCondition(e.target.checked)}
                className="mt-1 w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
              />
              <span className="text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: caseType === 'warranty' ? t('step3.warrantyAcceptCondition') : t('step3.acceptCondition') }} />
            </label>

            <div className="flex justify-between">
              <button
                onClick={() => setStep(2)}
                className="px-6 py-3 text-slate-600 hover:bg-slate-100 rounded-xl flex items-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                {t('common.back')}
              </button>
              <button
                onClick={() => setStep(4)}
                disabled={photos.length === 0 || !acceptCondition}
                className={`px-6 py-3 rounded-xl flex items-center gap-2 ${
                  photos.length === 0 || !acceptCondition
                    ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                {t('common.continue')}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Programar recogida MRW / Transporte propio */}
        {step === 4 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up">
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {effectiveShippingOption === 'pickup'
                ? t('step4.titlePickup')
                : t('step4.titleOwn')}
            </h2>
            <p className="text-slate-500 mb-6">
              {effectiveShippingOption === 'pickup'
                ? t('step4.subtitlePickup')
                : t('step4.subtitleOwn')}
            </p>

            {/* Banner informativo */}
            {effectiveShippingOption === 'own' ? (
              <div className={`rounded-xl p-4 mb-6 ${caseType === 'warranty' ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                <div className="flex items-center gap-3">
                  <Package className={`w-5 h-5 ${caseType === 'warranty' ? 'text-amber-600' : 'text-green-600'}`} />
                  <div>
                    <p className={`font-medium ${caseType === 'warranty' ? 'text-amber-800' : 'text-green-800'}`}>{t('step4.reminderOwn')}</p>
                    <p className={`text-sm ${caseType === 'warranty' ? 'text-amber-600' : 'text-green-600'}`}>
                      {caseType === 'warranty' ? t('step4.warrantyOwnReminder') : t('step4.reminderOwnRefund', { amount: formatMoney(productAmount) })}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-xl p-4 mb-6 bg-blue-50 border border-blue-200">
                <div className="flex items-center gap-3">
                  <Send className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-800">{t('step4.pickupBanner')}</p>
                    <p className="text-sm text-blue-700">{t('step4.pickupBannerDesc')}</p>
                    <p className="text-sm text-blue-600 mt-1">
                      {caseType === 'warranty' ? t('step4.warrantyPickupReminder') : t('step4.reminderPickupRefund', { amount: formatMoney(refundAmount) })}
                      {caseType !== 'warranty' && shippingCost > 0 ? t('step4.reminderPickupCost', { cost: shippingCost.toFixed(2) }) : ''}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Recogida MRW a domicilio */}
            {effectiveShippingOption === 'pickup' && (
              <div className="space-y-4 mb-6">

                {/* Dirección de recogida (pre-rellenada del pedido) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('step4.pickupAddressTitle')}</label>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    <p className="font-medium text-slate-900">{address.contact_name}</p>
                    <p className="text-sm text-slate-600">{address.street}</p>
                    <p className="text-sm text-slate-600">{address.postal_code} {address.city}</p>
                  </div>
                </div>

                {/* Selector de fecha */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('step4.pickupDateLabel')}</label>
                  <input
                    type="date"
                    value={pickupDate}
                    onChange={(e) => setPickupDate(e.target.value)}
                    min={(() => {
                      const d = new Date()
                      const dow = d.getDay()  // 0=domingo, 6=sábado
                      // Solicitud en fin de semana → recogida mínima el martes (no lunes)
                      if (dow === 6) d.setDate(d.getDate() + 3)       // sábado → martes
                      else if (dow === 0) d.setDate(d.getDate() + 2)  // domingo → martes
                      else d.setDate(d.getDate() + 1)                 // resto → mañana
                      return d.toISOString().split('T')[0]
                    })()}
                    max={(() => {
                      const d = new Date()
                      d.setDate(d.getDate() + 7)
                      return d.toISOString().split('T')[0]
                    })()}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 bg-white text-slate-900"
                  />
                  {pickupDate && (new Date(pickupDate).getDay() === 0 || new Date(pickupDate).getDay() === 6) && (
                    <p className="text-xs text-red-600 mt-1">{t('step4.weekendNotAvailable', 'No se puede programar recogida en fin de semana')}</p>
                  )}
                </div>

                {/* Selector de franja horaria */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('step4.timeSlotLabel')}</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setPickupTimeSlot('1')}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        pickupTimeSlot === '1'
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <p className={`font-semibold ${pickupTimeSlot === '1' ? 'text-blue-800' : 'text-slate-700'}`}>
                        {t('step4.timeSlotMorning')}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPickupTimeSlot('2')}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        pickupTimeSlot === '2'
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <p className={`font-semibold ${pickupTimeSlot === '2' ? 'text-blue-800' : 'text-slate-700'}`}>
                        {t('step4.timeSlotAfternoon')}
                      </p>
                    </button>
                  </div>
                </div>

                {/* Observaciones */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('step4.observationsLabel')}</label>
                  <textarea
                    value={pickupNotes}
                    onChange={(e) => setPickupNotes(e.target.value.slice(0, 200))}
                    placeholder={t('step4.observationsPlaceholder')}
                    rows={2}
                    maxLength={200}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 bg-white text-slate-900 placeholder:text-slate-400 resize-none"
                  />
                  <p className="text-xs text-slate-400 text-right mt-1">{pickupNotes.length}/200</p>
                </div>

                {/* Telefono de contacto */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('step4.contactPhone')}</label>
                  <input
                    type="tel"
                    value={address.phone}
                    onChange={(e) => setAddress(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Ej: 600123456"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 bg-white text-slate-900 placeholder:text-slate-400"
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-blue-800">
                    {t('step4.pickupImportant')}
                  </p>
                </div>
              </div>
            )}

            {/* Direccion de envio para transporte propio */}
            {effectiveShippingOption === 'own' && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="font-medium text-amber-800 mb-2">{t('step4.ownShippingAddress')}</p>
                <p className="text-amber-700">
                  JVS Informatica<br />
                  C/ Perez Galdos SN bajo<br />
                  39560 Unquera (Cantabria) - España<br />
                  Telefono: 942 71 97 35
                </p>
                <p className="text-sm text-amber-600 mt-2">
                  {t('step4.ownShippingImportant')}
                </p>
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setStep(3)}
                className="px-6 py-3 text-slate-600 hover:bg-slate-100 rounded-xl flex items-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                {t('common.back')}
              </button>
              <button
                onClick={() => setStep(5)}
                disabled={effectiveShippingOption === 'pickup' && (!pickupDate || new Date(pickupDate).getDay() === 0 || new Date(pickupDate).getDay() === 6)}
                className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {t('common.continue')}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Confirmacion */}
        {step === 5 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up">
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {caseType === 'warranty' ? t('step5.warrantyTitle', 'Descripcion del defecto') : t('step5.title')}
            </h2>
            <p className="text-slate-500 mb-6">
              {caseType === 'warranty' ? t('step5.warrantySubtitle', 'Describe el problema del producto para que podamos evaluar la garantia') : t('step5.subtitle')}
            </p>

            {caseType === 'warranty' ? (
              /* Garantía: solo descripción del defecto */
              <div className="space-y-4 mb-6">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-2">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">{t('step5.warrantyInfo', 'Solicitud de garantia')}</p>
                      <p className="text-sm text-amber-700 mt-1">
                        {t('step5.warrantyInfoDesc', 'Recogeremos el producto, lo evaluaremos y si la garantia procede, te enviaremos un reemplazo sin coste.')}
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('step5.defectDescLabel', 'Describe el defecto o problema')}
                  </label>
                  <textarea
                    value={reasonDetails}
                    onChange={(e) => setReasonDetails(e.target.value)}
                    placeholder={t('step5.defectDescPlaceholder', 'Explica que problema tiene el producto: cuando empezo, que sintomas presenta, etc.')}
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-amber-500 bg-white text-slate-900 placeholder:text-slate-400 resize-none"
                  />
                </div>
              </div>
            ) : (
              /* Devolución: motivo + detalles */
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('step5.reasonLabel')}</label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 bg-white text-slate-900 placeholder:text-slate-400"
                  >
                    <option value="">{t('step5.reasonPlaceholder')}</option>
                    {RETURN_REASONS.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('step5.detailsLabel')}</label>
                  <textarea
                    value={reasonDetails}
                    onChange={(e) => setReasonDetails(e.target.value)}
                    placeholder={t('step5.detailsPlaceholder')}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 bg-white text-slate-900 placeholder:text-slate-400 resize-none"
                  />
                </div>
              </div>
            )}

            {/* Método de reembolso - solo para devoluciones */}
            {caseType === 'return' && (isCardPayment ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">{t('step5.cardRefund')}</p>
                    <p className="text-sm text-green-600">{t('step5.cardRefundDesc')}</p>
                  </div>
                </div>
              </div>
            ) : !canUseCoupon ? (
              /* Sin cuenta PrestaShop: solo transferencia bancaria */
              <div className="mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-800">{t('step5.bankTransferTitle')}</p>
                      <p className="text-sm text-blue-600">{t('step5.bankTransferDesc')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-blue-900 mb-2">
                      {t('step5.ibanLabel')}
                    </label>
                    <input
                      type="text"
                      value={formatIbanInput(refundIban)}
                      onChange={(e) => setRefundIban(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                      placeholder="ES00 0000 0000 0000 0000 0000"
                      maxLength={39}
                      className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent bg-white text-slate-900 placeholder:text-slate-400 font-mono tracking-wider ${
                        refundIban.length >= 20
                          ? validateIban(refundIban)
                            ? 'border-green-400 focus:ring-green-500'
                            : 'border-red-300 focus:ring-red-500'
                          : 'border-blue-200 focus:ring-blue-500'
                      }`}
                    />
                    {refundIban.length >= 20 && !validateIban(refundIban) && (
                      <p className="text-xs text-red-600 mt-1">
                        {t('step5.ibanInvalid')}
                      </p>
                    )}
                    {refundIban.length >= 20 && validateIban(refundIban) && (
                      <p className="text-xs text-green-600 mt-1">
                        {t('step5.ibanValid')}
                      </p>
                    )}
                    {refundIban.length < 20 && (
                      <p className="text-xs text-blue-600 mt-1">
                        {t('step5.ibanHelp')}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-900 mb-2">
                      {t('step5.ibanHolderLabel')}
                    </label>
                    <input
                      type="text"
                      value={refundIbanHolder}
                      onChange={(e) => setRefundIbanHolder(e.target.value)}
                      placeholder={t('step5.ibanHolderPlaceholder')}
                      className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-slate-900 placeholder:text-slate-400"
                    />
                  </div>
                </div>
              </div>
            ) : (
              /* Cliente PrestaShop sin tarjeta: transferencia o cupón */
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-3">{t('step5.refundMethodQuestion')}</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Opción: Transferencia bancaria */}
                  <button
                    type="button"
                    onClick={() => setRefundPreference('transfer')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      refundPreference === 'transfer'
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        refundPreference === 'transfer' ? 'bg-blue-100' : 'bg-slate-100'
                      }`}>
                        <Building2 className={`w-5 h-5 ${
                          refundPreference === 'transfer' ? 'text-blue-600' : 'text-slate-500'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900">{t('step5.bankTransfer')}</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {t('step5.bankTransferOption')}
                        </p>
                      </div>
                      {refundPreference === 'transfer' && (
                        <CheckCircle className="w-5 h-5 text-blue-500 shrink-0" />
                      )}
                    </div>
                  </button>

                  {/* Opción: Cupón de descuento */}
                  <button
                    type="button"
                    onClick={() => { setRefundPreference('store_credit'); setRefundIban('') }}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      refundPreference === 'store_credit'
                        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        refundPreference === 'store_credit' ? 'bg-purple-100' : 'bg-slate-100'
                      }`}>
                        <Gift className={`w-5 h-5 ${
                          refundPreference === 'store_credit' ? 'text-purple-600' : 'text-slate-500'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900">{t('step5.storeCreditOption')}</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {t('step5.storeCreditDesc')}
                        </p>
                      </div>
                      {refundPreference === 'store_credit' && (
                        <CheckCircle className="w-5 h-5 text-purple-500 shrink-0" />
                      )}
                    </div>
                  </button>
                </div>

                {/* Campo IBAN cuando elige transferencia */}
                {refundPreference === 'transfer' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-blue-900 mb-2">
                        {t('step5.ibanLabel')}
                      </label>
                      <input
                        type="text"
                        value={formatIbanInput(refundIban)}
                        onChange={(e) => setRefundIban(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                        placeholder="ES00 0000 0000 0000 0000 0000"
                        maxLength={39}
                        className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent bg-white text-slate-900 placeholder:text-slate-400 font-mono tracking-wider ${
                          refundIban.length >= 20
                            ? validateIban(refundIban)
                              ? 'border-green-400 focus:ring-green-500'
                              : 'border-red-300 focus:ring-red-500'
                            : 'border-blue-200 focus:ring-blue-500'
                        }`}
                      />
                      {refundIban.length >= 20 && !validateIban(refundIban) && (
                        <p className="text-xs text-red-600 mt-1">
                          {t('step5.ibanInvalid')}
                        </p>
                      )}
                      {refundIban.length >= 20 && validateIban(refundIban) && (
                        <p className="text-xs text-green-600 mt-1">
                          {t('step5.ibanValid')}
                        </p>
                      )}
                      {refundIban.length < 20 && (
                        <p className="text-xs text-blue-600 mt-1">
                          {t('step5.ibanHelp')}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-900 mb-2">
                        {t('step5.ibanHolderLabel')}
                      </label>
                      <input
                        type="text"
                        value={refundIbanHolder}
                        onChange={(e) => setRefundIbanHolder(e.target.value)}
                        placeholder={t('step5.ibanHolderPlaceholder')}
                        className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-slate-900 placeholder:text-slate-400"
                      />
                    </div>
                  </div>
                )}

                {/* Info cupón */}
                {refundPreference === 'store_credit' && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                    <p className="text-sm text-purple-800" dangerouslySetInnerHTML={{ __html: t('step5.storeCreditInfo', { amount: formatMoney(refundAmount) }) }} />
                  </div>
                )}
              </div>
            ))}

            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <h3 className="font-medium text-slate-900 mb-3">{t('step5.summary')}</h3>
              <div className="space-y-2 text-sm">
                {caseType === 'warranty' && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('step5.requestType', 'Tipo de solicitud')}</span>
                    <span className="font-medium text-amber-600">{t('step5.warrantyType', 'Garantia')}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('step5.products', { count: selectedProducts.length })}</span>
                  <span className="font-medium">{formatMoney(productAmount)}</span>
                </div>
                {caseType === 'return' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('step5.shippingMethod')}</span>
                      <span className="font-medium">
                        {effectiveShippingOption === 'own'
                          ? t('step5.ownShipping')
                          : t('step5.pickupMRW')}
                      </span>
                    </div>
                    {effectiveShippingOption === 'pickup' && pickupDate && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-500">{t('step5.pickupDate')}</span>
                          <span className="font-medium">{new Date(pickupDate).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">{t('step5.pickupTimeSlot')}</span>
                          <span className="font-medium">{pickupTimeSlot === '1' ? t('step4.timeSlotMorning') : t('step4.timeSlotAfternoon')}</span>
                        </div>
                      </>
                    )}
                    {shippingCost > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">{t('step5.pickupCost')}</span>
                        <span className="font-medium text-red-600">-{formatMoney(shippingCost)}</span>
                      </div>
                    )}
                    {!isCardPayment && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">{t('step5.refundMethod')}</span>
                        <span className="font-medium">
                          {!canUseCoupon || refundPreference === 'transfer' ? t('step5.bankTransferMethod') : refundPreference === 'store_credit' ? t('step5.storeCreditMethod') : '-'}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold border-t border-slate-200 pt-2 mt-2">
                      <span>{t('step5.refund')}</span>
                      <span className="text-green-600">{formatMoney(refundAmount)}</span>
                    </div>
                  </>
                )}
                {caseType === 'warranty' && (
                  <div className="text-xs text-slate-500 border-t border-slate-200 pt-2 mt-2">
                    {t('step5.warrantySummaryNote', 'Envia el producto a nuestra direccion. Tras la evaluacion, recibiras un reemplazo si procede.')}
                  </div>
                )}
              </div>
            </div>

            {submitError && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 mb-6">
                <AlertCircle className="w-5 h-5 shrink-0" />
                <span className="text-sm">{submitError}</span>
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setStep(4)}
                className="px-6 py-3 text-slate-600 hover:bg-slate-100 rounded-xl flex items-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                {t('common.back')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || (caseType === 'warranty' ? !reasonDetails.trim() : (!reason || (!isCardPayment && !canUseCoupon && (!refundIban || !validateIban(refundIban))) || (canUseCoupon && !isCardPayment && !refundPreference) || (refundPreference === 'transfer' && (!refundIban || !validateIban(refundIban)))))}
                className={`px-6 py-3 text-white rounded-xl disabled:opacity-50 flex items-center gap-2 ${caseType === 'warranty' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}`}
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    {t('common.sending')}
                  </>
                ) : (
                  <>
                    {caseType === 'warranty' ? <Shield className="w-5 h-5" /> : <Send className="w-5 h-5" />}
                    {caseType === 'warranty' ? t('step5.submitWarranty', 'Solicitar garantia') : t('step5.submit')}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 6: Confirmacion */}
        {step === 6 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center animate-fade-in-up">
            <div className={`inline-flex p-5 rounded-full mb-6 animate-scale-in ${caseType === 'warranty' ? 'bg-amber-100' : 'bg-green-100'}`}>
              {caseType === 'warranty'
                ? <Shield className="w-14 h-14 text-amber-600 animate-pulse-soft" />
                : <CheckCircle className="w-14 h-14 text-green-600 animate-pulse-soft" />}
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {caseType === 'warranty'
                ? t('step6.warrantyTitle', 'Solicitud de garantia registrada')
                : hasPickupLabel
                  ? t('step6.titleShipment')
                  : t('step6.titleConfirmed')}
            </h2>
            <p className="text-slate-500 mb-6">
              {caseType === 'warranty'
                ? t('step6.warrantyDesc', 'Hemos recibido tu solicitud. Envia el producto a nuestra direccion con el codigo de garantia visible en el paquete.')
                : hasPickupLabel
                  ? t('step6.descPickup')
                  : t('step6.descOwn')}
            </p>
            {hasPickupLabel && pickupDate && (
              <p className="text-slate-500 mb-6 -mt-4">
                {t('step6.descPickupSchedule', {
                  date: new Date(pickupDate).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' }),
                  time: pickupTimeSlot === '1' ? t('step4.timeSlotMorning') : t('step4.timeSlotAfternoon')
                })}
              </p>
            )}

            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <p className="text-sm text-slate-500 mb-1">{t('step6.returnCodeLabel')}</p>
              <div className="flex items-center justify-center gap-2">
                <p className="text-3xl font-mono font-bold text-indigo-600">{returnCode}</p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(returnCode)
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                  }}
                  className="p-2 rounded-lg hover:bg-slate-200 transition-colors"
                  title={t('common.copyCode')}
                >
                  {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-slate-400" />}
                </button>
              </div>
              {pickupTracking && (
                <p className="text-sm text-slate-500 mt-2">
                  {t('step6.pickupTracking')} <span className="font-mono text-slate-700">{pickupTracking}</span>
                </p>
              )}
            </div>

            {/* Boton de etiqueta si hay recogida */}
            {hasPickupLabel && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Printer className="w-6 h-6 text-green-600" />
                  <p className="font-semibold text-green-800">{t('step6.labelTitle')}</p>
                </div>
                <p className="text-sm text-green-700 mb-4">
                  {t('step6.labelDesc')}
                </p>
                <a
                  href={`${API_BASE}/api/returns/${returnCode}/label`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 font-semibold"
                >
                  <Download className="w-5 h-5" />
                  {t('step6.downloadLabel')}
                </a>
              </div>
            )}

            {/* Recogida programada MRW */}
            {effectiveShippingOption === 'pickup' && pickupDate && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <Send className="w-6 h-6 text-blue-600" />
                  <p className="font-semibold text-blue-800">{t('step6.pickupScheduleTitle')}</p>
                </div>
                <p className="text-blue-700">{t('step6.pickupScheduleInfo')}</p>
                <div className="mt-2 space-y-1">
                  <p className="text-sm text-blue-800 font-semibold">
                    {new Date(pickupDate).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                  <p className="text-sm text-blue-700">
                    {pickupTimeSlot === '1' ? t('step4.timeSlotMorning') : t('step4.timeSlotAfternoon')}
                  </p>
                </div>
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-amber-800" dangerouslySetInnerHTML={{ __html: t('step6.writeCodeImportant', { code: returnCode }) }} />
            </div>

            <p className="text-sm text-slate-500 mb-6">
              {t('step6.saveCodeInfo')}
            </p>

            <a
              href={`/returns/track/${returnCode}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
            >
              <Search className="w-5 h-5" />
              {t('step6.checkStatus')}
            </a>
          </div>
        )}
      </div>

      {/* WhatsApp flotante */}
      <a
        href="https://wa.me/34644464960?text=Hola,%20tengo%20una%20consulta%20sobre%20una%20devolucion"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-green-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 hover:scale-110 transition-all z-50"
        title={t('common.whatsappTitle')}
      >
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {/* Footer */}
      <footer className="py-8 text-center text-sm text-slate-500">
        <p>{t('common.footer')}</p>
        <p className="mt-1">{t('common.footerContact')}</p>
      </footer>
    </div>
  )
}
